import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { categorizeError } from "@/lib/chat-utils";

interface ErrorCardProps {
  errorMessage?: string;
  errorCode?: string;
  onRetry?: () => void;
  onAlternativeAction?: (action: string) => void;
}

/**
 * Renders a categorized inline error card with retry and alternative action buttons.
 * Uses role="alert" for screen reader accessibility.
 * Supports all error categories: permission_denied, not_found, rate_limited,
 * stream_interrupted, and server_error (default).
 */
export function ErrorCard({
  errorMessage,
  errorCode,
  onRetry,
  onAlternativeAction,
}: ErrorCardProps) {
  const { t } = useTranslation();
  const errorCategory = categorizeError(errorCode);
  const Icon = errorCategory.icon;

  return (
    <Card
      role="alert"
      className={cn(
        "border-l-4 animate-in fade-in scale-in-95 duration-200",
        errorCategory.borderColor,
        errorCode === "server_error" || !errorCode
          ? "bg-destructive/10"
          : "bg-amber-50 dark:bg-amber-950/20",
      )}
    >
      <CardContent className="p-4 space-y-3">
        {/* Icon + heading row */}
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
          <span className="text-base font-semibold leading-tight">{t(errorCategory.i18nKey)}</span>
        </div>

        {/* Optional custom error message */}
        {errorMessage && errorMessage !== t(errorCategory.i18nKey) && (
          <p className="text-sm text-muted-foreground">{errorMessage}</p>
        )}

        {/* Action buttons row */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            aria-label={t("inspectionChat.errors.retry")}
          >
            {t("inspectionChat.errors.retry")}
          </Button>

          {errorCategory.alternatives.map((altKey) => (
            <Button
              key={altKey}
              variant="ghost"
              size="sm"
              onClick={() => onAlternativeAction?.(t(altKey))}
            >
              {t(altKey)}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
